import React, {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {Send, Languages} from 'lucide-react';

import {updateOnSend} from '../net/cache/update/send';
import {useSend} from '../net/query/Send.gql';
import {useBrowserId} from '../BrowserId';
import {
  useTranslation,
  normLanguage,
  Languages as LanguageOptions,
} from '../Translate';
import {useClientInfoCtx} from '../ClientInfoProvider';
import {errorMessage} from '../util/error';
import {Alert, AlertDescription} from '../components/ui/alert';
import {Button} from '../components/ui/button';
import {AutosizeTextarea} from '@/components/ui/autosize-textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {Spinner} from '../components/ui/spinner';
import {useToast} from '../components/ui/toast';

/**
 * Keyboard keyCode for the ENTER key.
 */
const KEY_ENTER = 13;

/**
 * Tidy up the message before sending.
 */
const clean = (msg: string) => {
  return msg
    .replace('\r\n', '\n')
    .replace(/\u00A0/g, ' ')
    .trim();
};

/**
 * Types to configure this component.
 */
export type ChatInputProps = {
  disable?: boolean;
};

/**
 * Message input box for the chat thread.
 */
export const ChatInput = ({disable}: ChatInputProps) => {
  const {accountId} = useParams();
  const deviceId = useBrowserId();
  const {showToast} = useToast();
  const [doTranslate, requestTranslation] = useState(false);
  const [msg, setMsg] = useState('');
  const [send, {error}] = useSend({
    update: updateOnSend,
  });
  const {loading, clientInfo} = useClientInfoCtx();
  const [lang, setLang] = useState('en');
  const translation = useTranslation({
    skip: !doTranslate,
    text: `[This message was translated by Google.] ${msg}`,
    into: lang,
  });

  useEffect(() => {
    if (!loading) {
      setLang(normLanguage(clientInfo?.language || 'en'));
    }
  }, [loading]);

  /**
   * Run the send message mutation!
   */
  const sendIt = (
    e: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent,
  ) => {
    // Prevent the native "enter" keyboard event from creating a newline.
    e.preventDefault();

    // alt+enter adds a newline to the message.
    if ('keyCode' in e && e.keyCode === KEY_ENTER && e.altKey) {
      setMsg((e.target as HTMLTextAreaElement).value + '\n');
      return;
    }

    // Remove weird characters / spacing from a message.
    const cleanMsg = clean(msg);

    // Don't send an empty message.
    if (!cleanMsg || !accountId) {
      return;
    }

    // Clean up the translation and decide whether to use it.
    const cleanTrans = clean(translation.translation || '');
    const includeTranslation =
      doTranslate &&
      !translation.error &&
      translation.translation &&
      lang !== 'en' &&
      !!cleanTrans;

    const finalMsg = includeTranslation
      ? `${cleanMsg}\n\n${cleanTrans}`
      : cleanMsg;

    send({
      variables: {
        content: finalMsg,
        fromDeviceId: deviceId,
        toAccountId: accountId,
      },
    });

    setMsg('');
    requestTranslation(false);
  };

  useEffect(() => {
    if (!error) {
      return;
    }

    showToast(`Your message was not sent: ${error}`, 'error');
  }, [error, showToast]);

  if (loading) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-2 border-t bg-background">
      <div className="flex items-center space-x-2">
        <div className="flex-1">
          <AutosizeTextarea
            placeholder="Type your message..."
            disabled={!accountId || !!disable}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendIt(e);
              }
            }}
            className="resize-none border-0 focus-visible:ring-0 shadow-none"
            minHeight={20}
            maxHeight={300}
          />
        </div>

        <div className="flex items-center space-x-1 flex-col">
          <Dialog open={doTranslate} onOpenChange={requestTranslation}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={!msg || !translation.canTranslate || !!disable}
                className="h-8 w-8"
              >
                <Languages className="h-4 w-4" />
                <span className="sr-only">Translate message</span>
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Translate message</DialogTitle>
                <DialogDescription>
                  Choose a language to translate your message into.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="language-select"
                    className="text-sm font-medium"
                  >
                    Target language
                  </label>
                  <Select value={lang} onValueChange={setLang}>
                    <SelectTrigger id="language-select">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {LanguageOptions.map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                          {language.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {translation.error ? (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {errorMessage(translation.error)}
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Original message
                  </label>
                  <div className="p-3 bg-muted rounded-md text-sm">{msg}</div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Translation</label>
                  <div className="p-3 bg-muted rounded-md text-sm min-h-[3rem] flex items-center">
                    {translation.translating ? (
                      <div className="flex items-center space-x-2">
                        <Spinner className="h-4 w-4" />
                        <span>Translating...</span>
                      </div>
                    ) : (
                      translation.translation || 'Translation will appear here'
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => requestTranslation(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendIt}
                    disabled={
                      translation.translating || !translation.translation
                    }
                  >
                    Send
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button
            variant="ghost"
            size="icon"
            disabled={!msg || !accountId || !!disable}
            onClick={sendIt}
            className="h-8 w-8"
          >
            <Send className="h-4 w-4" />
            <span className="sr-only">Send message</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
